import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIntegrationStatus } from "@/hooks/use-integration-status";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DataTable, ColumnDefinition } from "@/components/ui/data-table";
import { 
  Search, 
  Package, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  ExternalLink,
  Truck,
  FileText,
  Calendar,
  DollarSign,
  MapPin,
  User,
  Phone,
  Mail,
  Building,
  ClipboardList,
  ArrowRight,
  Printer,
  PackageCheck,
  Plus,
  Zap
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Types
interface SalesOrderLine {
  id: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityOrdered: number;
  quantityDispatched: number;
  quantityOutstanding: number;
  unitPrice: number;
  discountPercent?: number;
  discountAmount?: number;
  netAmount: number;
  taxRate?: number;
  taxRateName?: string;
  taxAmount: number;
  lineTotal: number;
  expectedDispatchDate: Date | null;
  projectCode?: string;
  projectName?: string;
}

interface SalesOrder {
  id: string;
  soNumber: string;
  customerCode: string;
  customerName: string;
  customerContact: string;
  customerEmail: string;
  customerPhone: string;
  orderDate: Date;
  expectedDispatchDate: Date;
  status: "open" | "partially-dispatched" | "fully-dispatched" | "cancelled" | "overdue";
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  totalValue: number;
  currency: string;
  taxBreakdown?: {
    taxRateName: string;
    taxRate: number;
    netAmount: number;
    taxAmount: number;
  }[];
  deliveryAddress: {
    line1: string;
    line2?: string;
    city: string;
    postcode: string;
    country: string;
  };
  paymentTerms: string;
  notes: string;
  lines: SalesOrderLine[];
  projectCode?: string;
  projectName?: string;
  syncedFrom: string;
  lastSyncDate: Date;
  createdBy: string;
  approvedBy: string;
  dispatchHistory: DispatchRecord[];
}

interface DispatchRecord {
  id: string;
  date: Date;
  dispatchedBy: string;
  reference: string;
  lines: {
    lineNumber: number;
    stockItemCode: string;
    quantityDispatched: number;
  }[];
}

// Mock data
const MOCK_SALES_ORDERS: SalesOrder[] = [
  {
    id: "1",
    soNumber: "SO-2024-001",
    customerCode: "CUST-001",
    customerName: "ABC Retail Ltd",
    customerContact: "Jane Smith",
    customerEmail: "jane@abcretail.com",
    customerPhone: "+44 20 1234 5678",
    orderDate: new Date("2024-01-15"),
    expectedDispatchDate: new Date("2024-02-15"),
    status: "partially-dispatched",
    subtotal: 15750.00,
    totalDiscount: 0,
    totalTax: 3150.00,
    totalValue: 18900.00,
    currency: "GBP",
    taxBreakdown: [
      {
        taxRateName: "Standard VAT",
        taxRate: 20,
        netAmount: 15750.00,
        taxAmount: 3150.00
      }
    ],
    deliveryAddress: {
      line1: "123 High Street",
      line2: "Unit 5",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Please deliver between 9am-5pm. Contact customer before delivery.",
    projectCode: "PRJ-003",
    projectName: "Client ABC - Custom Solution",
    lines: [
      {
        id: "l1",
        lineNumber: 1,
        stockItemCode: "WGT-A",
        stockItemName: "Widget A",
        description: "Premium quality widget - Model A",
        quantityOrdered: 150,
        quantityDispatched: 100,
        quantityOutstanding: 50,
        unitPrice: 45.00,
        discountPercent: 0,
        discountAmount: 0,
        netAmount: 6750.00,
        taxRate: 20,
        taxRateName: "Standard VAT",
        taxAmount: 1350.00,
        lineTotal: 8100.00,
        expectedDispatchDate: new Date("2024-02-15"),
        projectCode: "PRJ-003",
        projectName: "Client ABC - Custom Solution"
      },
      {
        id: "l2",
        lineNumber: 2,
        stockItemCode: "PART-C",
        stockItemName: "Component C",
        description: "Standard component C - Pack of 10",
        quantityOrdered: 200,
        quantityDispatched: 200,
        quantityOutstanding: 0,
        unitPrice: 22.50,
        discountPercent: 0,
        discountAmount: 0,
        netAmount: 4500.00,
        taxRate: 20,
        taxRateName: "Standard VAT",
        taxAmount: 900.00,
        lineTotal: 5400.00,
        expectedDispatchDate: new Date("2024-02-10"),
        projectCode: "PRJ-003",
        projectName: "Client ABC - Custom Solution"
      },
      {
        id: "l3",
        lineNumber: 3,
        stockItemCode: "RAW-X",
        stockItemName: "Raw Material X",
        description: "Raw material X - 25kg bags",
        quantityOrdered: 100,
        quantityDispatched: 0,
        quantityOutstanding: 100,
        unitPrice: 45.00,
        discountPercent: 0,
        discountAmount: 0,
        netAmount: 4500.00,
        taxRate: 20,
        taxRateName: "Standard VAT",
        taxAmount: 900.00,
        lineTotal: 5400.00,
        expectedDispatchDate: new Date("2024-02-20"),
        projectCode: "PRJ-003",
        projectName: "Client ABC - Custom Solution"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-20T10:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: [
      {
        id: "d1",
        date: new Date("2024-01-18"),
        dispatchedBy: "John Doe",
        reference: "DN-2024-001",
        lines: [
          { lineNumber: 1, stockItemCode: "WGT-A", quantityDispatched: 100 },
          { lineNumber: 2, stockItemCode: "PART-C", quantityDispatched: 200 }
        ]
      }
    ]
  },
  {
    id: "2",
    soNumber: "SO-2024-002",
    customerCode: "CUST-002",
    customerName: "XYZ Wholesale Corp",
    customerContact: "Tom Brown",
    customerEmail: "tom@xyzwholesale.com",
    customerPhone: "+44 20 9876 5432",
    orderDate: new Date("2024-01-20"),
    expectedDispatchDate: new Date("2024-01-25"),
    status: "overdue",
    totalValue: 8500.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "456 Industrial Park",
      city: "Manchester",
      postcode: "M1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Urgent order - please expedite dispatch",
    projectCode: "PRJ-005",
    projectName: "New Product Launch - Widget Pro",
    lines: [
      {
        id: "l4",
        lineNumber: 1,
        stockItemCode: "WGT-B",
        stockItemName: "Widget B",
        description: "Widget B - Standard model",
        quantityOrdered: 100,
        quantityDispatched: 0,
        quantityOutstanding: 100,
        unitPrice: 85.00,
        lineTotal: 8500.00,
        expectedDispatchDate: new Date("2024-01-25"),
        projectCode: "PRJ-005",
        projectName: "New Product Launch - Widget Pro"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-20T14:15:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "3",
    soNumber: "SO-2024-003",
    customerCode: "CUST-003",
    customerName: "Global Trading Co",
    customerContact: "Lisa Chen",
    customerEmail: "lisa@globaltrading.com",
    customerPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-01-22"),
    expectedDispatchDate: new Date("2024-02-28"),
    status: "open",
    totalValue: 25600.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "789 Commerce Street",
      line2: "Building B",
      city: "Birmingham",
      postcode: "B1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 45 days",
    notes: "Large order - may require multiple deliveries",
    lines: [
      {
        id: "l5",
        lineNumber: 1,
        stockItemCode: "GDG-B",
        stockItemName: "Gadget B",
        description: "Gadget B - Professional series",
        quantityOrdered: 200,
        quantityDispatched: 0,
        quantityOutstanding: 200,
        unitPrice: 128.00,
        lineTotal: 25600.00,
        expectedDispatchDate: new Date("2024-02-28")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-22T09:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "4",
    soNumber: "SO-2024-004",
    customerCode: "CUST-001",
    customerName: "ABC Retail Ltd",
    customerContact: "Jane Smith",
    customerEmail: "jane@abcretail.com",
    customerPhone: "+44 20 1234 5678",
    orderDate: new Date("2024-01-10"),
    expectedDispatchDate: new Date("2024-01-20"),
    status: "fully-dispatched",
    totalValue: 5400.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 High Street",
      line2: "Unit 5",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Standard delivery",
    lines: [
      {
        id: "l6",
        lineNumber: 1,
        stockItemCode: "PART-D",
        stockItemName: "Component D",
        description: "Component D - Economy pack",
        quantityOrdered: 300,
        quantityDispatched: 300,
        quantityOutstanding: 0,
        unitPrice: 18.00,
        lineTotal: 5400.00,
        expectedDispatchDate: new Date("2024-01-20")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-19T16:45:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: [
      {
        id: "d2",
        date: new Date("2024-01-19"),
        dispatchedBy: "Jane Smith",
        reference: "DN-2024-002",
        lines: [
          { lineNumber: 1, stockItemCode: "PART-D", quantityDispatched: 300 }
        ]
      }
    ]
  },
  {
    id: "5",
    soNumber: "SO-2024-005",
    customerCode: "CUST-004",
    customerName: "Food Distributors Ltd",
    customerContact: "Maria Garcia",
    customerEmail: "maria@fooddist.com",
    customerPhone: "+44 20 7777 8888",
    orderDate: new Date("2024-01-25"),
    expectedDispatchDate: new Date("2024-02-05"),
    status: "open",
    totalValue: 12300.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "456 Cold Storage Facility",
      city: "Leeds",
      postcode: "LS1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 14 days",
    notes: "Temperature controlled delivery required. Must arrive before 10am.",
    lines: [
      {
        id: "l7",
        lineNumber: 1,
        stockItemCode: "FOOD-A",
        stockItemName: "Food Product A",
        description: "Fresh food product A - Refrigerated",
        quantityOrdered: 150,
        quantityDispatched: 0,
        quantityOutstanding: 150,
        unitPrice: 82.00,
        lineTotal: 12300.00,
        expectedDispatchDate: new Date("2024-02-05")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T11:20:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "6",
    soNumber: "SO-2023-999",
    customerCode: "CUST-002",
    customerName: "XYZ Wholesale Corp",
    customerContact: "Tom Brown",
    customerEmail: "tom@xyzwholesale.com",
    customerPhone: "+44 20 9876 5432",
    orderDate: new Date("2023-12-15"),
    expectedDispatchDate: new Date("2024-01-05"),
    status: "cancelled",
    totalValue: 3200.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "456 Industrial Park",
      city: "Manchester",
      postcode: "M1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Cancelled due to customer request",
    lines: [
      {
        id: "l8",
        lineNumber: 1,
        stockItemCode: "PART-E",
        stockItemName: "Component E",
        description: "Component E - Special order",
        quantityOrdered: 80,
        quantityDispatched: 0,
        quantityOutstanding: 0,
        unitPrice: 40.00,
        lineTotal: 3200.00,
        expectedDispatchDate: new Date("2024-01-05")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-08T13:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  // Additional sales orders from stock transaction history
  {
    id: "7",
    soNumber: "SO-2024-012",
    customerCode: "CUST-005",
    customerName: "Tech Solutions Ltd",
    customerContact: "James Wilson",
    customerEmail: "james@techsolutions.com",
    customerPhone: "+44 20 1111 2222",
    orderDate: new Date("2024-01-05"),
    expectedDispatchDate: new Date("2024-01-15"),
    status: "fully-dispatched",
    totalValue: 2599.98,
    currency: "GBP",
    deliveryAddress: {
      line1: "10 Tech Park",
      city: "London",
      postcode: "EC1A 1BB",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Early adopter sale",
    lines: [
      {
        id: "l9",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 2,
        quantityDispatched: 2,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 2599.98,
        expectedDispatchDate: new Date("2024-01-15")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-16T09:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "8",
    soNumber: "SO-2024-018",
    customerCode: "CUST-006",
    customerName: "Enterprise Systems Inc",
    customerContact: "Rachel Green",
    customerEmail: "rachel@enterprise.com",
    customerPhone: "+44 20 3333 4444",
    orderDate: new Date("2024-01-12"),
    expectedDispatchDate: new Date("2024-01-22"),
    status: "fully-dispatched",
    totalValue: 9099.93,
    currency: "GBP",
    deliveryAddress: {
      line1: "50 Business Center",
      city: "Birmingham",
      postcode: "B2 4AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 45 days",
    notes: "Large order for new office setup",
    lines: [
      {
        id: "l10",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 7,
        quantityDispatched: 7,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 9099.93,
        expectedDispatchDate: new Date("2024-01-22")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-23T11:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "9",
    soNumber: "SO-2024-022",
    customerCode: "CUST-001",
    customerName: "ABC Corporation",
    customerContact: "David Lee",
    customerEmail: "david@abccorp.com",
    customerPhone: "+44 20 5555 6666",
    orderDate: new Date("2024-01-18"),
    expectedDispatchDate: new Date("2024-01-28"),
    status: "fully-dispatched",
    totalValue: 3899.97,
    currency: "GBP",
    deliveryAddress: {
      line1: "200 Corporate Plaza",
      city: "Manchester",
      postcode: "M3 3AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Standard sale",
    lines: [
      {
        id: "l11",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 3,
        quantityDispatched: 3,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 3899.97,
        expectedDispatchDate: new Date("2024-01-28")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-29T14:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "10",
    soNumber: "SO-2024-028",
    customerCode: "CUST-005",
    customerName: "Tech Solutions Ltd",
    customerContact: "James Wilson",
    customerEmail: "james@techsolutions.com",
    customerPhone: "+44 20 1111 2222",
    orderDate: new Date("2024-01-25"),
    expectedDispatchDate: new Date("2024-02-05"),
    status: "fully-dispatched",
    totalValue: 7799.94,
    currency: "GBP",
    deliveryAddress: {
      line1: "10 Tech Park",
      city: "London",
      postcode: "EC1A 1BB",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Repeat customer order",
    lines: [
      {
        id: "l12",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 6,
        quantityDispatched: 6,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 7799.94,
        expectedDispatchDate: new Date("2024-02-05")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-06T10:15:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "11",
    soNumber: "SO-2024-030",
    customerCode: "CUST-007",
    customerName: "Corporate Spaces Inc",
    customerContact: "Michael Brown",
    customerEmail: "michael@corpspaces.com",
    customerPhone: "+44 20 7777 8888",
    orderDate: new Date("2024-01-28"),
    expectedDispatchDate: new Date("2024-02-08"),
    status: "fully-dispatched",
    totalValue: 3742.50,
    currency: "GBP",
    deliveryAddress: {
      line1: "75 Office Complex",
      city: "Leeds",
      postcode: "LS1 2AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Office refurbishment project",
    lines: [
      {
        id: "l13",
        lineNumber: 1,
        stockItemCode: "STK002",
        stockItemName: "Office Chair Ergonomic",
        description: "Ergonomic office chair with lumbar support",
        quantityOrdered: 15,
        quantityDispatched: 15,
        quantityOutstanding: 0,
        unitPrice: 249.50,
        lineTotal: 3742.50,
        expectedDispatchDate: new Date("2024-02-08")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-09T09:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "12",
    soNumber: "SO-2024-032",
    customerCode: "CUST-008",
    customerName: "Digital Workspace Ltd",
    customerContact: "Emma Watson",
    customerEmail: "emma@digitalworkspace.com",
    customerPhone: "+44 20 9999 0000",
    orderDate: new Date("2024-02-01"),
    expectedDispatchDate: new Date("2024-02-12"),
    status: "fully-dispatched",
    totalValue: 5199.96,
    currency: "GBP",
    deliveryAddress: {
      line1: "30 Innovation Hub",
      city: "Bristol",
      postcode: "BS1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Office upgrade project",
    lines: [
      {
        id: "l14",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 4,
        quantityDispatched: 4,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 5199.96,
        expectedDispatchDate: new Date("2024-02-12")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-13T13:45:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "13",
    soNumber: "SO-2024-038",
    customerCode: "CUST-006",
    customerName: "Enterprise Systems Inc",
    customerContact: "Rachel Green",
    customerEmail: "rachel@enterprise.com",
    customerPhone: "+44 20 3333 4444",
    orderDate: new Date("2024-02-08"),
    expectedDispatchDate: new Date("2024-02-18"),
    status: "fully-dispatched",
    totalValue: 6499.95,
    currency: "GBP",
    deliveryAddress: {
      line1: "50 Business Center",
      city: "Birmingham",
      postcode: "B2 4AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 45 days",
    notes: "Large corporate order",
    lines: [
      {
        id: "l15",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 5,
        quantityDispatched: 5,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 6499.95,
        expectedDispatchDate: new Date("2024-02-18")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-19T10:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "14",
    soNumber: "SO-2024-040",
    customerCode: "CUST-009",
    customerName: "Schools Supply Co",
    customerContact: "Sarah Miller",
    customerEmail: "sarah@schoolssupply.com",
    customerPhone: "+44 20 4444 5555",
    orderDate: new Date("2024-02-12"),
    expectedDispatchDate: new Date("2024-02-22"),
    status: "fully-dispatched",
    totalValue: 599.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "100 Education Center",
      city: "Oxford",
      postcode: "OX1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Back to school bulk order",
    lines: [
      {
        id: "l16",
        lineNumber: 1,
        stockItemCode: "STK003",
        stockItemName: "A4 Paper Ream (500 sheets)",
        description: "High quality A4 paper for printing",
        quantityOrdered: 100,
        quantityDispatched: 100,
        quantityOutstanding: 0,
        unitPrice: 5.99,
        lineTotal: 599.00,
        expectedDispatchDate: new Date("2024-02-22")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-23T11:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "15",
    soNumber: "SO-2024-042",
    customerCode: "CUST-001",
    customerName: "ABC Corporation",
    customerContact: "David Lee",
    customerEmail: "david@abccorp.com",
    customerPhone: "+44 20 5555 6666",
    orderDate: new Date("2024-02-15"),
    expectedDispatchDate: new Date("2024-02-25"),
    status: "fully-dispatched",
    totalValue: 2599.98,
    currency: "GBP",
    deliveryAddress: {
      line1: "200 Corporate Plaza",
      city: "Manchester",
      postcode: "M3 3AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Standard sale",
    lines: [
      {
        id: "l17",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 2,
        quantityDispatched: 2,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 2599.98,
        expectedDispatchDate: new Date("2024-02-25")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-26T14:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "16",
    soNumber: "SO-2024-044",
    customerCode: "CUST-010",
    customerName: "Office Interiors Ltd",
    customerContact: "Tom Harris",
    customerEmail: "tom@officeinteriors.com",
    customerPhone: "+44 20 6666 7777",
    orderDate: new Date("2024-02-18"),
    expectedDispatchDate: new Date("2024-02-28"),
    status: "fully-dispatched",
    totalValue: 2495.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "25 Design Quarter",
      city: "London",
      postcode: "SW1A 2AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Bulk furniture order",
    lines: [
      {
        id: "l18",
        lineNumber: 1,
        stockItemCode: "STK002",
        stockItemName: "Office Chair Ergonomic",
        description: "Ergonomic office chair with lumbar support",
        quantityOrdered: 10,
        quantityDispatched: 10,
        quantityOutstanding: 0,
        unitPrice: 249.50,
        lineTotal: 2495.00,
        expectedDispatchDate: new Date("2024-02-28")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-29T09:15:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "17",
    soNumber: "SO-2024-045",
    customerCode: "CUST-005",
    customerName: "Tech Solutions Ltd",
    customerContact: "James Wilson",
    customerEmail: "james@techsolutions.com",
    customerPhone: "+44 20 1111 2222",
    orderDate: new Date("2024-02-20"),
    expectedDispatchDate: new Date("2024-03-01"),
    status: "fully-dispatched",
    totalValue: 3899.97,
    currency: "GBP",
    deliveryAddress: {
      line1: "10 Tech Park",
      city: "London",
      postcode: "EC1A 1BB",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Bulk order for corporate client",
    lines: [
      {
        id: "l19",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Laptop Dell XPS 15",
        description: "Dell XPS 15 - High performance laptop",
        quantityOrdered: 3,
        quantityDispatched: 3,
        quantityOutstanding: 0,
        unitPrice: 1299.99,
        lineTotal: 3899.97,
        expectedDispatchDate: new Date("2024-03-01")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-03-02T10:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  },
  {
    id: "18",
    soNumber: "SO-2024-046",
    customerCode: "CUST-011",
    customerName: "Office Supplies Direct",
    customerContact: "Lisa Anderson",
    customerEmail: "lisa@officesupplies.com",
    customerPhone: "+44 20 8888 9999",
    orderDate: new Date("2024-02-22"),
    expectedDispatchDate: new Date("2024-03-04"),
    status: "fully-dispatched",
    totalValue: 299.50,
    currency: "GBP",
    deliveryAddress: {
      line1: "15 Supplies Warehouse",
      city: "Liverpool",
      postcode: "L1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 14 days",
    notes: "Regular customer order",
    lines: [
      {
        id: "l20",
        lineNumber: 1,
        stockItemCode: "STK003",
        stockItemName: "A4 Paper Ream (500 sheets)",
        description: "High quality A4 paper for printing",
        quantityOrdered: 50,
        quantityDispatched: 50,
        quantityOutstanding: 0,
        unitPrice: 5.99,
        lineTotal: 299.50,
        expectedDispatchDate: new Date("2024-03-04")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-03-05T08:45:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    dispatchHistory: []
  }
];

export function SalesOrdersPage() {
  const { navigateTo, pageParams } = useNavigation();
  const integrationStatus = useIntegrationStatus();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("sales-orders");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [customerFilter, setCustomerFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [selectedSO, setSelectedSO] = useState<SalesOrder | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showQuickAddDrawer, setShowQuickAddDrawer] = useState(false);
  
  // Pagination and sorting state
  const [sortColumn, setSortColumn] = useState<keyof SalesOrder | string>("soNumber");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Handle navigation from transaction history
  useEffect(() => {
    if (pageParams?.transactionId) {
      // Extract the reference number from the transaction ID
      // The transactionId is actually the reference number (e.g., "SO-2024-045")
      const reference = pageParams.transactionId;
      
      // Find the sales order with this reference
      const so = MOCK_SALES_ORDERS.find(order => order.soNumber === reference);
      
      if (so) {
        setSelectedSO(so);
        setShowDetailDrawer(true);
        toast.success(`Opened ${reference}`);
      } else {
        toast.error(`Sales order ${reference} not found`);
      }
    }
  }, [pageParams]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const openSOs = MOCK_SALES_ORDERS.filter(so => so.status === "open").length;
    const awaitingDispatch = MOCK_SALES_ORDERS.filter(so => 
      so.status === "open" || so.status === "partially-dispatched"
    ).length;
    const partiallyDispatched = MOCK_SALES_ORDERS.filter(so => so.status === "partially-dispatched").length;
    const overdue = MOCK_SALES_ORDERS.filter(so => so.status === "overdue").length;

    return { openSOs, awaitingDispatch, partiallyDispatched, overdue };
  }, []);

  // Get unique customers for filter
  const customers = useMemo(() => {
    const uniqueCustomers = Array.from(new Set(MOCK_SALES_ORDERS.map(so => so.customerName)));
    return uniqueCustomers.sort();
  }, []);

  // Get unique projects/cost centers for filter
  const projects = useMemo(() => {
    const uniqueProjects = Array.from(
      new Set(
        MOCK_SALES_ORDERS
          .filter(so => so.projectCode && so.projectName)
          .map(so => JSON.stringify({ code: so.projectCode, name: so.projectName }))
      )
    ).map(str => JSON.parse(str));
    return uniqueProjects.sort((a, b) => a.code.localeCompare(b.code));
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = MOCK_SALES_ORDERS;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(so =>
        so.soNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        so.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        so.customerCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (so.projectCode && so.projectCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (so.projectName && so.projectName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      if (statusFilter === "outstanding") {
        // Outstanding includes: open, partially-dispatched, and overdue
        filtered = filtered.filter(so => 
          so.status === "open" || 
          so.status === "partially-dispatched" || 
          so.status === "overdue"
        );
      } else {
        filtered = filtered.filter(so => so.status === statusFilter);
      }
    }

    // Filter by customer
    if (customerFilter !== "all") {
      filtered = filtered.filter(so => so.customerName === customerFilter);
    }

    // Filter by project/cost center
    if (projectFilter !== "all") {
      filtered = filtered.filter(so => so.projectCode === projectFilter);
    }

    return filtered;
  }, [searchTerm, statusFilter, customerFilter, projectFilter]);

  // Sort filtered data
  const sortedData = useMemo(() => {
    const data = [...filteredData];
    
    data.sort((a, b) => {
      let aValue: any = a[sortColumn as keyof SalesOrder];
      let bValue: any = b[sortColumn as keyof SalesOrder];
      
      // Handle date sorting
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }
      
      // Handle string sorting (case insensitive)
      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return data;
  }, [filteredData, sortColumn, sortDirection]);

  // Paginate sorted data
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, customerFilter, projectFilter]);

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Format date and time
  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Open</Badge>;
      case "partially-dispatched":
        return <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">Partially Dispatched</Badge>;
      case "fully-dispatched":
        return <Badge variant="success" accent="success">Fully Dispatched</Badge>;
      case "overdue":
        return <Badge variant="destructive">Overdue</Badge>;
      case "cancelled":
        return <Badge variant="secondary">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get days until/overdue
  const getDaysUntilDispatch = (date: Date) => {
    const days = Math.floor((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Handle row click
  // Handle sorting
  const handleSort = (column: keyof SalesOrder | string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRowClick = (so: SalesOrder) => {
    setSelectedSO(so);
    setShowDetailDrawer(true);
  };

  // Handle dispatch goods
  const handleDispatchGoods = (so: SalesOrder) => {
    toast.info("Dispatch functionality coming soon");
  };

  // Handle sync
  const handleSync = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: "Syncing with Access Financials...",
        success: "Sales orders synced successfully",
        error: "Failed to sync sales orders",
      }
    );
  };

  // Calculate line fulfillment percentage
  const getLineFulfillmentPercentage = (line: SalesOrderLine) => {
    return (line.quantityDispatched / line.quantityOrdered) * 100;
  };

  // Calculate overall SO fulfillment
  const getSOFulfillmentPercentage = (so: SalesOrder) => {
    const totalOrdered = so.lines.reduce((sum, line) => sum + line.quantityOrdered, 0);
    const totalDispatched = so.lines.reduce((sum, line) => sum + line.quantityDispatched, 0);
    return totalOrdered > 0 ? (totalDispatched / totalOrdered) * 100 : 0;
  };

  // Define table columns
  const columns: ColumnDefinition<SalesOrder>[] = [
    {
      id: "soNumber",
      header: "SO Number",
      sortable: true,
      cellClassName: "font-medium font-mono",
      cell: (so) => so.soNumber,
    },
    {
      id: "customerName",
      header: "Customer",
      sortable: true,
      cell: (so) => (
        <div>
          <div className="font-medium">{so.customerName}</div>
          <div className="text-sm text-muted-foreground">{so.customerCode}</div>
        </div>
      ),
    },
    {
      id: "projectCode",
      header: "Project/Cost Center",
      sortable: true,
      cell: (so) =>
        so.projectCode && so.projectName ? (
          <div>
            <div className="font-medium text-sm">{so.projectCode}</div>
            <div className="text-xs text-muted-foreground">{so.projectName}</div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: "orderDate",
      header: "Order Date",
      sortable: true,
      cellClassName: "font-mono text-sm",
      cell: (so) => formatDate(so.orderDate),
    },
    {
      id: "expectedDispatchDate",
      header: "Expected Dispatch",
      sortable: true,
      cell: (so) => {
        const daysUntil = getDaysUntilDispatch(so.expectedDispatchDate);
        return (
          <div>
            <div className="font-mono text-sm">{formatDate(so.expectedDispatchDate)}</div>
            {so.status !== "fully-dispatched" && so.status !== "cancelled" && (
              <div className="text-xs text-muted-foreground">
                {daysUntil < 0 ? (
                  <span className="text-destructive font-medium">
                    {Math.abs(daysUntil)} days overdue
                  </span>
                ) : daysUntil === 0 ? (
                  <span className="text-yellow-600 font-medium">Due today</span>
                ) : (
                  <span>In {daysUntil} days</span>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      cell: (so) => getStatusBadge(so.status),
    },
    {
      id: "totalValue",
      header: "Total Value",
      sortable: true,
      headerClassName: "text-right",
      cellClassName: "text-right font-mono font-medium",
      cell: (so) => formatCurrency(so.totalValue, so.currency),
    },
    {
      id: "lines",
      header: "Items",
      headerClassName: "text-center",
      cellClassName: "text-center",
      cell: (so) => <Badge variant="outline">{so.lines.length}</Badge>,
    },
    {
      id: "fulfillment",
      header: "Fulfillment",
      headerClassName: "text-center",
      cellClassName: "text-center",
      cell: (so) => {
        const fulfillment = getSOFulfillmentPercentage(so);
        return (
          <div className="flex items-center justify-center gap-2">
            <div className="w-16 bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  fulfillment === 100
                    ? "bg-green-600"
                    : fulfillment > 0
                    ? "bg-yellow-600"
                    : "bg-gray-300"
                }`}
                style={{ width: `${fulfillment}%` }}
              />
            </div>
            <span className="text-xs font-medium">{Math.round(fulfillment)}%</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (so) => (
        <div className="flex justify-end gap-2">
          {so.status !== "fully-dispatched" && so.status !== "cancelled" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDispatchGoods(so);
              }}
            >
              <PackageCheck className="h-4 w-4 mr-1" />
              Dispatch
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              toast.info("Print functionality coming soon");
            }}
          >
            <Printer className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Sales Orders</h1>
              {integrationStatus.isFinancialsIntegrated && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Synced from Access Financials
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-2">
              View and track sales orders{integrationStatus.isFinancialsIntegrated ? " synced from your ERP system" : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <HelpButton onClick={toggleHelp} />
            {/* <Button onClick={() => setShowQuickAddDrawer(true)} variant="default">
              <Plus className="h-4 w-4 mr-2" />
              Quick Add Sale Order
            </Button> */}
            <Button onClick={handleSync} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StaticMetricCardAlt
          title="Open SOs"
          value={stats.openSOs.toString()}
          icon={Package}
          variant="info"
          changeLabel="Awaiting dispatch"
        />
        <StaticMetricCardAlt
          title="Awaiting Dispatch"
          value={stats.awaitingDispatch.toString()}
          icon={Truck}
          variant="info"
          changeLabel="Open or partially dispatched"
        />
        <StaticMetricCardAlt
          title="Partially Dispatched"
          value={stats.partiallyDispatched.toString()}
          icon={Clock}
          variant="warning"
          changeLabel="Awaiting remaining items"
        />
        <StaticMetricCardAlt
          title="Overdue"
          value={stats.overdue.toString()}
          icon={AlertTriangle}
          variant="error"
          changeLabel="Past expected dispatch date"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sales Orders</CardTitle>
              <CardDescription>
                View sales orders synced from Access Financials
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by SO number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="outstanding">Outstanding</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="partially-dispatched">Partially Dispatched</SelectItem>
                <SelectItem value="fully-dispatched">Fully Dispatched</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Customer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map((customer) => (
                  <SelectItem key={customer} value={customer}>
                    {customer}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="Project/Cost Center" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects/Cost Centers</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.code} value={project.code}>
                    {project.code} - {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data Table */}
          <DataTable
            data={paginatedData}
            columns={columns}
            sortConfig={{ column: sortColumn, direction: sortDirection }}
            onSort={handleSort}
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={sortedData.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onRowClick={handleRowClick}
            emptyState={
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No sales orders found</h3>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search or filters
                </p>
              </div>
            }
            striped
          />
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={showDetailDrawer} onOpenChange={setShowDetailDrawer}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-3">
          {selectedSO && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SheetTitle className="font-mono">{selectedSO.soNumber}</SheetTitle>
                    {getStatusBadge(selectedSO.status)}
                  </div>
                </div>
                <SheetDescription>
                  Sales order for {selectedSO.customerName}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Sync Information */}
                <div className="bg-muted/50 p-3 rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Last synced:</span>
                      <span className="font-medium">{formatDateTime(selectedSO.lastSyncDate)}</span>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {selectedSO.syncedFrom}
                    </Badge>
                  </div>
                </div>

                {/* Order Information */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Order Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Order Date</Label>
                      <p className="font-medium font-mono">{formatDate(selectedSO.orderDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Expected Dispatch</Label>
                      <p className="font-medium font-mono">{formatDate(selectedSO.expectedDispatchDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Created By</Label>
                      <p className="font-medium">{selectedSO.createdBy}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Approved By</Label>
                      <p className="font-medium">{selectedSO.approvedBy}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Payment Terms</Label>
                      <p className="font-medium">{selectedSO.paymentTerms}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Total Value (inc. Tax)</Label>
                      <p className="font-medium text-lg">{formatCurrency(selectedSO.totalValue, selectedSO.currency)}</p>
                      <p className="text-xs text-muted-foreground">
                        Net: {formatCurrency(selectedSO.subtotal, selectedSO.currency)} + 
                        Tax: {formatCurrency(selectedSO.totalTax, selectedSO.currency)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Customer Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Contact:</span>
                      <span className="font-medium">{selectedSO.customerContact}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Email:</span>
                      <a href={`mailto:${selectedSO.customerEmail}`} className="font-medium text-primary hover:underline">
                        {selectedSO.customerEmail}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Phone:</span>
                      <a href={`tel:${selectedSO.customerPhone}`} className="font-medium text-primary hover:underline">
                        {selectedSO.customerPhone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Delivery Address
                  </h3>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p>{selectedSO.deliveryAddress.line1}</p>
                    {selectedSO.deliveryAddress.line2 && <p>{selectedSO.deliveryAddress.line2}</p>}
                    <p>{selectedSO.deliveryAddress.city}</p>
                    <p>{selectedSO.deliveryAddress.postcode}</p>
                    <p>{selectedSO.deliveryAddress.country}</p>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    Line Items
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Ordered</TableHead>
                          <TableHead className="text-right">Dispatched</TableHead>
                          <TableHead className="text-right">Outstanding</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead className="text-right">Tax</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedSO.lines.map((line) => {
                          const fulfillment = getLineFulfillmentPercentage(line);
                          return (
                            <TableRow key={line.id}>
                              <TableCell className="font-medium">{line.lineNumber}</TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium text-primary">{line.stockItemCode}</div>
                                  <div className="text-sm text-muted-foreground">{line.stockItemName}</div>
                                  {line.description && (
                                    <div className="text-xs text-muted-foreground mt-1">{line.description}</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono">{line.quantityOrdered}</TableCell>
                              <TableCell className="text-right">
                                <div className="font-mono">{line.quantityDispatched}</div>
                                <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                                  <div 
                                    className={`h-1.5 rounded-full ${
                                      fulfillment === 100 ? 'bg-green-600' :
                                      fulfillment > 0 ? 'bg-yellow-600' : 'bg-gray-300'
                                    }`}
                                    style={{ width: `${fulfillment}%` }}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {line.quantityOutstanding > 0 ? (
                                  <span className="text-yellow-600 font-medium">{line.quantityOutstanding}</span>
                                ) : (
                                  <span className="text-green-600 font-medium">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(line.unitPrice, selectedSO.currency)}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(line.netAmount, selectedSO.currency)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="font-mono">{formatCurrency(line.taxAmount, selectedSO.currency)}</div>
                                {line.taxRateName && (
                                  <div className="text-xs text-muted-foreground">{line.taxRateName} ({line.taxRate}%)</div>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono font-medium">
                                {formatCurrency(line.lineTotal, selectedSO.currency)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow className="border-t-2">
                          <TableCell colSpan={6} className="text-right font-medium">Subtotal:</TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            {formatCurrency(selectedSO.subtotal, selectedSO.currency)}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                        {selectedSO.taxBreakdown && selectedSO.taxBreakdown.map((tax, index) => (
                          <TableRow key={index}>
                            <TableCell colSpan={6} className="text-right text-muted-foreground">
                              {tax.taxRateName} ({tax.taxRate}%):
                            </TableCell>
                            <TableCell colSpan={2} className="text-right font-mono">
                              {formatCurrency(tax.taxAmount, selectedSO.currency)}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-medium border-t-2">
                          <TableCell colSpan={6} className="text-right text-lg">Total:</TableCell>
                          <TableCell colSpan={2} className="text-right font-mono text-lg">
                            {formatCurrency(selectedSO.totalValue, selectedSO.currency)}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Dispatch History */}
                {selectedSO.dispatchHistory.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Dispatch History
                    </h3>
                    <div className="space-y-2">
                      {selectedSO.dispatchHistory.map((record) => (
                        <div key={record.id} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="font-mono">{record.reference}</Badge>
                              <span className="text-sm text-muted-foreground">by {record.dispatchedBy}</span>
                            </div>
                            <span className="text-sm text-muted-foreground font-mono">
                              {formatDateTime(record.date)}
                            </span>
                          </div>
                          <div className="text-sm space-y-1">
                            {record.lines.map((line, idx) => (
                              <div key={idx} className="flex justify-between">
                                <span className="text-muted-foreground">Line {line.lineNumber} ({line.stockItemCode}):</span>
                                <span className="font-medium">+{line.quantityDispatched} units</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedSO.notes && (
                  <div>
                    <h3 className="font-semibold mb-3">Notes</h3>
                    <p className="text-sm bg-muted p-3 rounded-md">{selectedSO.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  {selectedSO.status !== "fully-dispatched" && selectedSO.status !== "cancelled" && (
                    <Button 
                      className="flex-1"
                      onClick={() => {
                        handleDispatchGoods(selectedSO);
                        setShowDetailDrawer(false);
                      }}
                    >
                      <PackageCheck className="h-4 w-4 mr-2" />
                      Dispatch Goods
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className={selectedSO.status === "fully-dispatched" || selectedSO.status === "cancelled" ? "flex-1" : ""}
                    onClick={() => toast.info("Print functionality coming soon")}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print SO
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => toast.info("View in Access Financials")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in ERP
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Quick Add Sale Order Drawer */}
      <Sheet open={showQuickAddDrawer} onOpenChange={setShowQuickAddDrawer}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2">
              <SheetTitle>Quick Add Sale Order</SheetTitle>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <Zap className="h-3 w-3 mr-1" />
                Future Implementation
              </Badge>
            </div>
            <SheetDescription>
              This will be an integrated micro frontend for quickly creating sales orders
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Vision Card */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  Micro Frontend Integration
                </CardTitle>
                <CardDescription>
                  This feature will be powered by a dedicated micro frontend application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <FileText className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Standalone Application</div>
                      <div className="text-sm text-muted-foreground">
                        Independent micro frontend that can be developed and deployed separately
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Zap className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Quick Order Creation</div>
                      <div className="text-sm text-muted-foreground">
                        Streamlined form for rapid sales order entry with smart defaults and customer lookup
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <RefreshCw className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Real-time Sync</div>
                      <div className="text-sm text-muted-foreground">
                        Automatically syncs with Access Financials and updates the main sales order list
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Validation & Stock Check</div>
                      <div className="text-sm text-muted-foreground">
                        Built-in validation for pricing, stock availability, and customer credit limits
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview of Future Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Form Preview</CardTitle>
                <CardDescription>
                  A glimpse of the quick add interface (non-functional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 opacity-60 pointer-events-none">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer *</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Order Date *</Label>
                    <Input type="date" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expected Dispatch Date *</Label>
                    <Input type="date" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Project (Optional)</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Order Lines</Label>
                  <div className="border rounded-lg p-4 space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-sm font-medium">
                      <div className="col-span-4">Stock Item</div>
                      <div className="col-span-2">Quantity</div>
                      <div className="col-span-2">Unit Price</div>
                      <div className="col-span-2">Discount %</div>
                      <div className="col-span-2">Line Total</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <Select disabled>
                          <SelectTrigger>
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="0" disabled />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="0.00" disabled />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="0" disabled />
                      </div>
                      <div className="col-span-2">
                        <Input placeholder="0.00" disabled />
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" disabled>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Line
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Delivery Address</Label>
                  <Input placeholder="Street address" disabled className="mb-2" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" disabled />
                    <Input placeholder="Postcode" disabled />
                    <Input placeholder="Country" disabled />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input placeholder="Order notes or special instructions" disabled />
                </div>
              </CardContent>
            </Card>

            {/* Technical Details */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Technical Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Module Federation:</span>
                    <span className="text-muted-foreground">Webpack 5 Module Federation for runtime integration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Independent Deployment:</span>
                    <span className="text-muted-foreground">Can be updated without redeploying main app</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Shared Context:</span>
                    <span className="text-muted-foreground">Access to authentication and integration status</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Event Communication:</span>
                    <span className="text-muted-foreground">Publishes events when orders are created</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="bg-muted p-4 rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-3">
                This feature is part of the roadmap for enhanced sales order management
              </p>
              <Button variant="outline" disabled>
                <FileText className="h-4 w-4 mr-2" />
                Coming Soon
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />
    </div>
  );
}
